/* ProgressDealSheet.jsx — iter-214B · Deal Progression Control Centre.
 *
 * The progression engine of ChainFlow. Authorised parties open this
 * right-side drawer from the canonical Deal Room to confirm
 * milestones, log progress, add blockers, and move the transaction
 * forward — directly. This is NOT an activity feed. Every action
 * here mutates the milestones state, advances the top transaction
 * timeline, logs an immutable activity event, and refreshes the
 * operational rails.
 *
 * Doctrine:
 *   - Calm. Operational. Never CRM / dashboard / kanban.
 *   - Optimistic UI — flips happen instantly; server reconciles.
 *   - Tenure-aware — freehold (7 stages) vs leasehold (11 stages).
 *   - Permissions enforced server-side via `can_complete[]` payload.
 *
 * Surface contract:
 *   <ProgressDealSheet
 *     open              boolean
 *     onClose           () => void
 *     deal              { id, name, stage, tenure, exchange_confidence, ... }
 *     milestones        { items, can_complete } from /api/v2/deals/:id/milestones
 *     resolver          (deal, milestones) → { steps, currentIdx, completedSet }
 *     onComplete        (milestoneId) → Promise<void>
 *     onRefresh         () => void                 — refetches milestones
 *     participants      [{ user_id, role, name, ... }]
 *   />
 */
import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import {
  X, Check, Clock, AlertTriangle, Loader2, MessageSquare, Upload,
  Sparkles, ChevronRight, ArrowRight, Pin, Bell, Calendar, FileText, Paperclip,
} from "lucide-react";
import { api } from "../../lib/api";

/* Owner labels by milestone id — calm, never aggressive. */
const MILESTONE_OWNER = {
  searches_returned:        "Buyer solicitor",
  enquiries_resolved:       "Buyer solicitor",
  signed_contract:          "Buyer solicitor",
  draft_contract:           "Seller solicitor",
  leasehold_enquiries:      "Buyer solicitor",
  mortgage_offer:           "Broker",
  deposit_confirmed:        "Buyer solicitor",
  lender_authority:         "Broker",
  completion_date:          "Agent",
  chain_aligned:            "Agent",
  buyer_confirmation:       "Buyer",
  survey_complete:          "Buyer",
  management_pack_requested:"Seller solicitor",
  management_pack_received: "Managing agent",
  management_pack:          "Managing agent",
  exchange_confirmed:       "Agent",
};

/* iter-214B — Optional milestone definitions PER stage id.
 * When the server doesn't return a milestone for a given step, the
 * UI surfaces a calm "Not started" card so the operator can see the
 * full transaction shape at a glance. */
const STAGE_DEFAULT_MILESTONE = {
  // freehold
  offer:                  { id: "offer_agreed",            label: "Offer Agreed",            owner: "Agent" },
  aml:                    { id: "aml_complete",            label: "AML Checks",              owner: "Agent" },
  searches:               { id: "searches_returned",       label: "Searches Returned",       owner: "Buyer solicitor" },
  mortgage_offer:         { id: "mortgage_offer",          label: "Mortgage Offer Received", owner: "Broker" },
  contracts:              { id: "signed_contract",         label: "Contracts Signed",        owner: "Buyer solicitor" },
  exchange:               { id: "exchange_confirmed",      label: "Exchange Confirmed",      owner: "Agent" },
  completion:             { id: "completion_done",         label: "Completion",              owner: "Agent" },
  // leasehold-only
  draft_contract:         { id: "draft_contract",          label: "Draft Contract Issued",   owner: "Seller solicitor" },
  mgmt_pack_requested:    { id: "management_pack_requested", label: "Management Pack Requested", owner: "Seller solicitor" },
  mgmt_pack_received:     { id: "management_pack_received",  label: "Management Pack Received",  owner: "Managing agent" },
  leasehold_enquiries:    { id: "leasehold_enquiries",     label: "Leasehold Enquiries",     owner: "Buyer solicitor" },
};

/* Status tone palette. */
const STATUS_TONE = {
  done:      { ring: "ring-emerald-200/70",  surface: "bg-emerald-50/40", chip: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/70", icon: "text-emerald-600", label: "Complete" },
  active:    { ring: "ring-violet-300/70",   surface: "bg-violet-50/40",  chip: "bg-violet-50 text-violet-700 ring-1 ring-violet-200/70",     icon: "text-violet-600",  label: "In progress" },
  waiting:   { ring: "ring-amber-200/70",    surface: "bg-amber-50/40",   chip: "bg-amber-50 text-amber-700 ring-1 ring-amber-200/70",        icon: "text-amber-600",   label: "Waiting" },
  blocked:   { ring: "ring-rose-200/70",     surface: "bg-rose-50/40",    chip: "bg-rose-50 text-rose-700 ring-1 ring-rose-200/70",           icon: "text-rose-600",    label: "Blocked" },
  upcoming:  { ring: "ring-slate-200/70",    surface: "bg-white",         chip: "bg-slate-50 text-slate-500 ring-1 ring-slate-200/70",        icon: "text-slate-300",   label: "Not started" },
};

/* iter-215 — Step → primary server-side milestone id.
 *
 * The journey steps (offer, aml, searches, mortgage_offer, contracts,
 * exchange, completion, …) are operator-facing labels; the
 * server-side MILESTONES catalogue uses internal ids (searches_returned,
 * mortgage_offer, signed_contract, …). This map lets each StageCard
 * resolve its canonical server milestone so target dates + uploads
 * file against the right immutable activity row.
 *
 * Steps without a server milestone (e.g. offer, aml, exchange,
 * completion) intentionally map to null — the StageCard then hides
 * the Set-target/Upload affordances so we never surface buttons
 * that would 404 against `/milestones/:id/target`. */
const STEP_TO_SERVER_MILESTONE = {
  offer:                null,             // synthetic, no server record
  aml:                  null,             // synthetic
  searches:             "searches_returned",
  mortgage_offer:       "mortgage_offer",
  contracts:            "signed_contract",
  exchange:             null,             // explicit exchange_confirmed not in catalogue
  completion:           "completion_date",
  // leasehold-specific journey steps
  draft_contract:       null,
  mgmt_pack_requested:  null,
  mgmt_pack_received:   null,
  leasehold_enquiries:  "enquiries_resolved",
};

function findMilestoneForStep(stepId, milestones) {
  const items = Array.isArray(milestones?.items) ? milestones.items : [];
  // Match by id first (legacy direct mapping).
  const direct = items.find((m) => m && m.id === stepId);
  if (direct) return direct;
  // Match by explicit step alias if the server attached one.
  const aliased = items.find((m) => m && m.step === stepId);
  if (aliased) return aliased;
  // iter-215 — Resolve through the canonical step → server mapping.
  const serverId = STEP_TO_SERVER_MILESTONE[stepId];
  if (serverId) {
    return items.find((m) => m && m.id === serverId) || null;
  }
  return null;
}

function deriveStatus(item, isActive, isCompleted) {
  if (item?.state === "blocked" || item?.blocker) return "blocked";
  if (item?.state === "done" || item?.completed_at) return "done";
  if (item?.state === "waiting" || item?.waiting_on) return "waiting";
  if (isCompleted) return "done";
  if (isActive)    return "active";
  return "upcoming";
}

/* Header summary atoms. */
function ConfidenceBadge({ value }) {
  const v = Number(value || 0);
  const tone = v >= 70 ? "text-emerald-700 bg-emerald-50 ring-emerald-200/60"
             : v >= 40 ? "text-violet-700 bg-violet-50 ring-violet-200/60"
             :          "text-amber-700 bg-amber-50 ring-amber-200/60";
  return (
    <span className={`inline-flex items-center gap-1 px-2 h-[22px] rounded-md text-[10.5px] font-bold tracking-tight ring-1 ${tone}`} data-testid="progress-confidence">
      <Sparkles className="h-3 w-3" strokeWidth={2.2} />
      Exchange confidence {v}%
    </span>
  );
}

function MomentumDot({ momentum }) {
  const tone = momentum === "improving" ? "bg-emerald-500"
             : momentum === "slipping"  ? "bg-rose-500"
             :                            "bg-slate-400";
  const label = momentum === "improving" ? "Momentum improving"
              : momentum === "slipping"  ? "Momentum slipping"
              :                            "Momentum steady";
  return (
    <span className="inline-flex items-center gap-1.5 text-[11.5px] text-slate-600 font-medium" data-testid="progress-momentum">
      <span className={`h-1.5 w-1.5 rounded-full ${tone}`} aria-hidden />
      {label}
    </span>
  );
}

/* Sheet shell — matches DealRoomSheets.SlideOver visual language but
 * widened to 600px for the progression engine. */
function SheetShell({ open, onClose, children }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[85]" role="dialog" aria-modal="true" data-testid="progress-deal-sheet">
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onClose}
        className="absolute inset-0 bg-slate-900/35 backdrop-blur-[3px]"
        style={{ animation: "cf-pds-fade 220ms ease-out" }}
        data-testid="progress-deal-sheet-backdrop"
      />
      <aside
        className="absolute right-0 top-0 bottom-0 w-full sm:max-w-[600px] bg-white shadow-2xl flex flex-col"
        style={{ animation: "cf-pds-in 280ms cubic-bezier(0.16,1,0.3,1)" }}
      >
        {children}
      </aside>
      <style>{`
        @keyframes cf-pds-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes cf-pds-in   { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @media (max-width: 640px) {
          aside { left: 0 !important; top: auto !important; height: 95dvh !important; max-width: none !important; border-top-left-radius: 24px; border-top-right-radius: 24px; }
          @keyframes cf-pds-in { from { transform: translateY(100%); } to { transform: translateY(0); } }
        }
      `}</style>
    </div>,
    document.body,
  );
}

/* Individual stage card. */
function StageCard({
  step, milestone, status, canComplete, stageRequired, onComplete, onAddUpdate, onAddBlocker,
  onSetTargetDate, onUploadDocument, linkedDocuments,
}) {
  const tone = STATUS_TONE[status] || STATUS_TONE.upcoming;
  const owner = milestone?.owner || MILESTONE_OWNER[milestone?.id] || STAGE_DEFAULT_MILESTONE[step.id]?.owner || "—";
  const completedAt = milestone?.completed_at;
  const lastNote = milestone?.last_note || milestone?.note || milestone?.blocker || null;
  const targetDate = milestone?.target_date || milestone?.due_at;
  const [busy, setBusy] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [dateDraft, setDateDraft] = useState("");
  const [savingDate, setSavingDate] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [stageError, setStageError] = useState(null);
  const fileInputRef = useRef(null);

  const canAct = canComplete && status !== "done" && (milestone?.id || step.id);
  /* iter-215 — target-date + upload affordances use the same
   * permission gate as Mark complete. Read-only users see the
   * card but get no action surface. The completed state also
   * hides these affordances — completed milestones are immutable.
   *
   * Additional guard — only render the new actions when the server
   * has a real milestone backing this step (milestone?.id). The
   * canonical MILESTONES catalogue only covers Exchange-Eve items,
   * so synthetic stage ids (offer_agreed, aml_complete, etc.)
   * would 404 against /milestones/:id/target. Hiding the buttons
   * for those steps keeps the surface honest. */
  const hasServerMilestone = !!milestone?.id;
  const canTargetOrUpload = canComplete && status !== "done" && hasServerMilestone;

  // Reset the draft to the current target whenever the picker opens.
  useEffect(() => {
    if (datePickerOpen) {
      setDateDraft(targetDate ? String(targetDate).slice(0, 10) : "");
      setStageError(null);
    }
  }, [datePickerOpen, targetDate]);

  const handleSaveDate = useCallback(async () => {
    if (savingDate) return;
    const mid = milestone?.id || STAGE_DEFAULT_MILESTONE[step.id]?.id || step.id;
    setSavingDate(true);
    setStageError(null);
    try {
      await onSetTargetDate?.(mid, dateDraft || null);
      setDatePickerOpen(false);
    } catch (err) {
      setStageError(
        err?.response?.data?.detail || err?.message || "Couldn't update target date",
      );
    } finally {
      setSavingDate(false);
    }
  }, [dateDraft, milestone, onSetTargetDate, savingDate, step.id]);

  const handleUploadClick = useCallback(() => {
    if (uploadBusy) return;
    setStageError(null);
    fileInputRef.current?.click();
  }, [uploadBusy]);

  const handleFileChosen = useCallback(async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    const mid = milestone?.id || STAGE_DEFAULT_MILESTONE[step.id]?.id || step.id;
    setUploadBusy(true);
    setStageError(null);
    try {
      await onUploadDocument?.(mid, file);
    } catch (err) {
      setStageError(
        err?.response?.data?.detail || err?.message || "Upload failed",
      );
    } finally {
      setUploadBusy(false);
      // Reset the input so the same file can be re-selected after
      // a failed attempt.
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [milestone, onUploadDocument, step.id]);

  const StatusIcon = status === "done"     ? Check
                   : status === "active"   ? Clock
                   : status === "waiting"  ? Clock
                   : status === "blocked"  ? AlertTriangle
                   :                          Clock;

  const docsForMilestone = Array.isArray(linkedDocuments) ? linkedDocuments : [];

  return (
    <div
      className={`relative rounded-2xl bg-white px-4 py-3.5 ring-1 ${tone.ring} ${tone.surface} transition-shadow hover:shadow-[0_18px_36px_-26px_rgba(15,23,42,0.18)]`}
      data-testid={`progress-stage-${step.id}`}
      data-status={status}
    >
      <div className="flex items-start gap-3">
        <div className={`h-8 w-8 rounded-xl bg-white ring-1 ring-slate-100 flex items-center justify-center shrink-0 ${tone.icon}`}>
          <StatusIcon className="h-4 w-4" strokeWidth={2.2} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h3 className="text-[13.5px] font-semibold text-slate-900 leading-tight" data-testid={`progress-stage-label-${step.id}`}>
              {milestone?.label || step.label}
            </h3>
            <span className={`inline-flex items-center px-1.5 h-[18px] rounded-md text-[10px] font-bold ${tone.chip}`} data-testid={`progress-stage-status-${step.id}`}>
              {tone.label}
            </span>
          </div>
          <div className="mt-1 flex items-center gap-2 flex-wrap text-[11px] text-slate-500">
            <span className="font-medium text-slate-600">{owner}</span>
            {completedAt && (
              <>
                <span className="text-slate-300">·</span>
                <span className="tabular-nums" data-testid={`progress-stage-completed-${step.id}`}>
                  Completed {new Date(completedAt).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                </span>
              </>
            )}
            {!completedAt && targetDate && (
              <>
                <span className="text-slate-300">·</span>
                <span className="tabular-nums" data-testid={`progress-stage-target-${step.id}`}>
                  Target {new Date(targetDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}
                </span>
              </>
            )}
          </div>
          {lastNote && (
            <p className="mt-1.5 text-[11.5px] text-slate-600 leading-snug" data-testid={`progress-stage-note-${step.id}`}>
              {lastNote}
            </p>
          )}
          {/* iter-215 — Linked documents (calm inline chips). */}
          {docsForMilestone.length > 0 && (
            <div
              className="mt-2 flex flex-wrap gap-1.5"
              data-testid={`progress-stage-docs-${step.id}`}
            >
              {docsForMilestone.slice(0, 4).map((d) => (
                <span
                  key={d.id}
                  className="inline-flex items-center gap-1 h-6 px-2 rounded-md bg-slate-50 ring-1 ring-slate-200/70 text-[10.5px] font-medium text-slate-600 max-w-[180px]"
                  title={d.name}
                  data-testid={`progress-stage-doc-${step.id}-${d.id}`}
                >
                  <Paperclip className="h-2.5 w-2.5 text-slate-400" />
                  <span className="truncate">{d.name}</span>
                </span>
              ))}
              {docsForMilestone.length > 4 && (
                <span className="inline-flex items-center h-6 px-1.5 rounded-md text-[10.5px] text-slate-400 font-medium">
                  +{docsForMilestone.length - 4}
                </span>
              )}
            </div>
          )}
          {canAct && (
            <div className="mt-2.5 flex items-center gap-2 flex-wrap" data-testid={`progress-stage-actions-${step.id}`}>
              {stageRequired && stageRequired.total > 0 && stageRequired.remaining > 0 ? (
                /* iter-alignment · P0 · Manual Mark-complete
                 * disabled for stages with outstanding required
                 * checklist rows.  The button is replaced (not
                 * hidden) by a calm progress read-out so the
                 * operator knows exactly how many required rows
                 * remain.  Stage completion becomes automatic
                 * the moment every required row is ticked — the
                 * canonical Current Stage checklist is the sole
                 * completion surface.  Optional rows are
                 * excluded from the count per the stage-
                 * completion contract. */
                <span
                  className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg bg-slate-50 ring-1 ring-slate-200 text-slate-600 text-[11px] font-semibold"
                  data-testid={`progress-stage-required-progress-${step.id}`}
                  data-required-total={stageRequired.total}
                  data-required-remaining={stageRequired.remaining}
                  title="Tick required milestones in the Current Stage checklist. The stage completes automatically once every required row is done."
                >
                  {stageRequired.done} of {stageRequired.total} required complete · {stageRequired.remaining} remaining
                </span>
              ) : (
                <button
                  type="button"
                  onClick={async () => {
                    if (busy) return;
                    const mid = milestone?.id || STAGE_DEFAULT_MILESTONE[step.id]?.id || step.id;
                    setBusy(true);
                    try { await onComplete?.(mid); } finally { setBusy(false); }
                  }}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-[11px] font-semibold transition disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid={`progress-stage-complete-${step.id}`}
                >
                  {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                  Mark complete
                </button>
              )}
              <button
                type="button"
                onClick={() => onAddUpdate?.(step, milestone)}
                className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg bg-white hover:bg-slate-50 ring-1 ring-slate-200 text-slate-700 text-[11px] font-semibold transition"
                data-testid={`progress-stage-update-${step.id}`}
              >
                <MessageSquare className="h-3 w-3 text-slate-500" />
                Add update
              </button>
              <button
                type="button"
                onClick={() => onAddBlocker?.(step, milestone)}
                className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg bg-white hover:bg-slate-50 ring-1 ring-slate-200 text-slate-700 text-[11px] font-semibold transition"
                data-testid={`progress-stage-blocker-${step.id}`}
              >
                <AlertTriangle className="h-3 w-3 text-amber-500" />
                Add blocker
              </button>
              {/* iter-215 — Set target date. Calm inline toggle.
                  Only rendered when the server tracks this milestone
                  (otherwise the POST would 404 against the canonical
                  MILESTONES catalogue). */}
              {onSetTargetDate && hasServerMilestone && (
                <button
                  type="button"
                  onClick={() => setDatePickerOpen((v) => !v)}
                  className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg bg-white hover:bg-slate-50 ring-1 ring-slate-200 text-slate-700 text-[11px] font-semibold transition"
                  data-testid={`progress-stage-set-target-${step.id}`}
                  aria-expanded={datePickerOpen}
                >
                  <Calendar className="h-3 w-3 text-slate-500" />
                  {targetDate ? "Update target" : "Set target date"}
                </button>
              )}
              {/* iter-215 — Upload supporting document.
                  Gated identically — only surfaces when there's a
                  real milestone to file the document against. */}
              {onUploadDocument && hasServerMilestone && (
                <>
                  <button
                    type="button"
                    onClick={handleUploadClick}
                    disabled={uploadBusy}
                    className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg bg-white hover:bg-slate-50 ring-1 ring-slate-200 text-slate-700 text-[11px] font-semibold transition disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid={`progress-stage-upload-${step.id}`}
                  >
                    {uploadBusy ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Upload className="h-3 w-3 text-slate-500" />
                    )}
                    Upload document
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileChosen}
                    className="sr-only"
                    data-testid={`progress-stage-upload-input-${step.id}`}
                  />
                </>
              )}
            </div>
          )}
          {canTargetOrUpload && datePickerOpen && (
            <div
              className="mt-2 flex items-center gap-2 flex-wrap bg-slate-50/70 ring-1 ring-slate-200/70 rounded-xl px-2.5 py-2"
              data-testid={`progress-stage-date-picker-${step.id}`}
            >
              <input
                type="date"
                value={dateDraft}
                onChange={(e) => setDateDraft(e.target.value)}
                disabled={savingDate}
                className="h-7 px-2 rounded-md ring-1 ring-slate-200 bg-white text-[11.5px] text-slate-800 outline-none focus:ring-violet-300"
                data-testid={`progress-stage-date-input-${step.id}`}
              />
              <button
                type="button"
                onClick={handleSaveDate}
                disabled={savingDate}
                className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md bg-violet-600 hover:bg-violet-700 text-white text-[11px] font-semibold disabled:opacity-60 transition"
                data-testid={`progress-stage-date-save-${step.id}`}
              >
                {savingDate && <Loader2 className="h-3 w-3 animate-spin" />}
                Save
              </button>
              <button
                type="button"
                onClick={() => setDatePickerOpen(false)}
                disabled={savingDate}
                className="text-[11px] font-medium text-slate-500 hover:text-slate-700"
                data-testid={`progress-stage-date-cancel-${step.id}`}
              >
                Cancel
              </button>
            </div>
          )}
          {/* iter-215 — Calm inline failure surface — never crashes the page. */}
          {stageError && (
            <p
              className="mt-2 text-[11px] text-rose-600 leading-snug"
              role="alert"
              data-testid={`progress-stage-error-${step.id}`}
            >
              {stageError}
            </p>
          )}
          {!canAct && status !== "done" && (
            <p className="mt-2 text-[10.5px] text-slate-400 italic">Read-only — your role doesn't progress this milestone.</p>
          )}
        </div>
      </div>
    </div>
  );
}

/* The drawer itself. */
export function ProgressDealSheet({
  open, onClose, deal, milestones, resolver, onComplete, onRefresh,
  participants, documents, onSetTargetDate, onUploadDocument,
}) {
  const [savingNote, setSavingNote] = useState(false);
  const [composer, setComposer] = useState({ step: null, kind: null, value: "" });

  /* iter-215 — Index linked documents by milestone_id for fast
   * lookup inside each StageCard. Tolerates malformed payloads. */
  const docsByMilestone = useMemo(() => {
    const map = {};
    if (!Array.isArray(documents)) return map;
    documents.forEach((d) => {
      const mid = d?.milestone_id;
      if (!mid) return;
      if (!map[mid]) map[mid] = [];
      map[mid].push(d);
    });
    return map;
  }, [documents]);

  /* iter-214B-FIX — Always return a valid progress object.
   *   - resolver missing            → empty rails (defensive)
   *   - resolver throws             → calm empty fallback
   * The drawer NEVER crashes the page; if data resolution fails it
   * surfaces a friendly empty-state inside the drawer only. */
  const progress = useMemo(() => {
    const empty = { steps: [], currentIdx: 0, completedSet: new Set() };
    if (!resolver) return empty;
    try {
      const res = resolver(deal || null, milestones || null);
      if (!res || !Array.isArray(res.steps)) return empty;
      return {
        steps: res.steps,
        currentIdx: Number.isFinite(res.currentIdx) ? res.currentIdx : 0,
        completedSet: res.completedSet instanceof Set ? res.completedSet : new Set(),
      };
    } catch (err) {
      if (typeof console !== "undefined" && console.error) {
        console.error("[ProgressDealSheet] resolver threw — rendering empty rail:", err);
      }
      return empty;
    }
  }, [deal, milestones, resolver]);

  const canComplete = (milestoneId) => {
    if (!milestoneId) return false;
    const list = milestones?.can_complete;
    if (!Array.isArray(list)) return true;  // server-permissive default
    return list.includes(milestoneId);
  };

  const safeDeal    = deal || {};
  const safeTenure  = String(
    safeDeal?.tenure ||
    safeDeal?.property?.tenure ||
    safeDeal?.property_tenure ||
    "freehold"
  ).toLowerCase();
  const dealTitle   = safeDeal?.name || safeDeal?.title || safeDeal?.address || "Untitled deal";
  const stage       = String(safeDeal?.stage || "offer").replace(/_/g, " ");
  const confidence  = Number(safeDeal?.exchange_confidence ?? safeDeal?.confidence ?? 50) || 0;
  const momentum    = safeDeal?.momentum ||
                      (safeDeal?.severity === "rose" ? "slipping"
                       : safeDeal?.severity === "emerald" ? "improving"
                       : "steady");
  const lastUpdate  = safeDeal?.last_progression_at || safeDeal?.last_activity_at || safeDeal?.updated_at;
  const hasRail     = progress.steps.length > 0;

  const submitComposer = useCallback(async () => {
    if (!composer.step || !composer.value.trim() || !safeDeal?.id) return;
    setSavingNote(true);
    try {
      try {
        await api.post(`/api/deals/${safeDeal.id}/notes`, {
          body: `[${composer.step.label}] ${composer.value.trim()}`,
          kind: composer.kind === "blocker" ? "blocker" : "progress_update",
          milestone_id: composer.step.id,
        });
      } catch (notesErr) {
        // Graceful fallback — log onto activity timeline directly.
        try {
          await api.post(`/api/v2/deals/${safeDeal.id}/activity`, {
            headline: `${composer.kind === "blocker" ? "Blocker noted" : "Update logged"} on ${composer.step.label}`,
            description: composer.value.trim(),
          });
        } catch (actErr) { /* both endpoints failed — toast still informs */ }
      }
      toast.success(composer.kind === "blocker" ? "Blocker noted" : "Update logged");
      setComposer({ step: null, kind: null, value: "" });
      onRefresh?.();
    } catch (err) {
      toast.error("Couldn't log update");
    } finally {
      setSavingNote(false);
    }
  }, [composer, safeDeal, onRefresh]);

  return (
    <SheetShell open={open} onClose={onClose}>
      {/* Header — premium tight summary. */}
      <header className="px-5 md:px-6 pt-5 pb-4 border-b border-slate-100">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-violet-600 mb-1">Progress deal</p>
            <h2 className="text-[17px] md:text-[18px] font-bold tracking-[-0.01em] text-slate-900 leading-tight truncate" data-testid="progress-title">
              {dealTitle}
            </h2>
            <p className="text-[12px] text-slate-500 mt-0.5 leading-snug">
              Coordinate milestones, confirm progress, and move the transaction forward.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 rounded-lg inline-flex items-center justify-center text-slate-400 hover:text-slate-900 hover:bg-slate-50 transition-colors"
            data-testid="progress-deal-sheet-close"
          >
            <X size={14} strokeWidth={2.4} />
          </button>
        </div>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1 px-2 h-[22px] rounded-md bg-violet-50 text-violet-700 ring-1 ring-violet-200/70 text-[10.5px] font-bold capitalize" data-testid="progress-current-stage">
            <span className="h-1.5 w-1.5 rounded-full bg-violet-500" aria-hidden />
            {stage} · In progress
          </span>
          <ConfidenceBadge value={confidence} />
          <MomentumDot momentum={momentum} />
          {lastUpdate && (
            <span className="text-[11px] text-slate-400 tabular-nums" data-testid="progress-last-update">
              Updated {new Date(lastUpdate).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
        </div>
      </header>

      {/* Body — vertical journey */}
      <div className="flex-1 overflow-y-auto px-5 md:px-6 py-5">
        {/* iter-214B-FIX — Empty rail fallback. If the resolver
            returned no steps (older seed · missing tenure · payload
            shape we don't recognise) we still render the drawer with
            a calm "Progression temporarily unavailable" surface so
            the operator is never staring at a blank panel. */}
        {!hasRail ? (
          <div className="rounded-2xl bg-slate-50/60 ring-1 ring-slate-200/70 px-5 py-8 text-center" data-testid="progress-empty-state">
            <p className="text-[13px] font-semibold text-slate-700">Progression temporarily unavailable</p>
            <p className="mt-1 text-[11.5px] text-slate-500 leading-snug">
              We couldn't resolve the milestone progression for this deal. Please refresh the page or
              try again shortly.
            </p>
          </div>
        ) : (
        <div className="space-y-2.5" data-testid="progress-journey-list" data-tenure={safeTenure}>
          {progress.steps.map((step, i) => {
            if (!step || !step.id) return null;
            const milestone = findMilestoneForStep(step.id, milestones);
            const isComplete = i < progress.currentIdx;
            const isActive   = i === progress.currentIdx;
            const status = deriveStatus(milestone, isActive, isComplete);
            const stagedMid = milestone?.id || STAGE_DEFAULT_MILESTONE[step.id]?.id || step.id;
            /* iter-alignment · P0 · Stage cannot be manually
             * completed while required checklist milestones
             * remain (2026-08-18).  The Progression sheet is a
             * secondary surface — the canonical Current Stage
             * checklist owns stage-completion, and required rows
             * MUST be ticked one-by-one so the audit trail
             * records who / when for each.  We compute the
             * required-only progress from the same
             * `milestones.items` SSOT the checklist consumes so
             * both surfaces cannot disagree.  Optional rows are
             * excluded per the stage-completion contract locked
             * in `test_iter_alignment_p_current_stage_required_only_counts`. */
            const _stageItems = Array.isArray(milestones?.items)
              ? milestones.items.filter((m) => m && m.stage === step.id && m.optional !== true)
              : [];
            const _stageDone = _stageItems.filter(
              (m) => m.state === "done" || !!m.completed_at,
            ).length;
            const stageRequired = {
              total:     _stageItems.length,
              done:      _stageDone,
              remaining: Math.max(0, _stageItems.length - _stageDone),
            };
            return (
              <StageCard
                key={step.id}
                step={step}
                milestone={milestone}
                status={status}
                canComplete={canComplete(stagedMid)}
                stageRequired={stageRequired}
                onComplete={onComplete}
                onAddUpdate={(s, m) => setComposer({ step: s, kind: "update", value: m?.last_note || "" })}
                onAddBlocker={(s) => setComposer({ step: s, kind: "blocker", value: "" })}
                onSetTargetDate={onSetTargetDate}
                onUploadDocument={onUploadDocument}
                linkedDocuments={docsByMilestone[stagedMid] || []}
              />
            );
          })}
        </div>
        )}
      </div>

      {/* Inline composer (Add update / Add blocker) */}
      {composer.step && (
        <div className="border-t border-slate-100 bg-slate-50/50 px-5 md:px-6 py-3.5" data-testid="progress-composer">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] font-semibold text-slate-700">
              {composer.kind === "blocker" ? "Add blocker on " : "Add update on "}
              <span className="text-violet-700">{composer.step.label}</span>
            </span>
            <button
              type="button"
              onClick={() => setComposer({ step: null, kind: null, value: "" })}
              className="text-[11px] text-slate-400 hover:text-slate-700"
              data-testid="progress-composer-cancel"
            >
              Cancel
            </button>
          </div>
          <textarea
            value={composer.value}
            onChange={(e) => setComposer((c) => ({ ...c, value: e.target.value }))}
            placeholder={composer.kind === "blocker" ? "What's blocking this milestone?" : "What's the progress update?"}
            rows={2}
            className="w-full rounded-xl bg-white border border-slate-200 px-3 py-2 text-[12.5px] text-slate-900 placeholder:text-slate-400 outline-none focus:border-violet-300 focus:ring-2 focus:ring-violet-100 resize-none"
            data-testid="progress-composer-input"
            autoFocus
          />
          <div className="mt-2 flex items-center justify-end">
            <button
              type="button"
              onClick={submitComposer}
              disabled={savingNote || !composer.value.trim()}
              className="inline-flex items-center gap-1.5 h-7 px-3 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-[11px] font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="progress-composer-submit"
            >
              {savingNote ? <Loader2 className="h-3 w-3 animate-spin" /> : <ArrowRight className="h-3 w-3" />}
              {composer.kind === "blocker" ? "Log blocker" : "Log update"}
            </button>
          </div>
        </div>
      )}

      {/* Sticky footer — calm summary + close */}
      <footer className="px-5 md:px-6 py-3.5 border-t border-slate-100 bg-white flex items-center justify-between" data-testid="progress-sheet-footer">
        <span className="text-[11px] text-slate-500">
          {progress.completedSet.size}/{progress.steps.length} milestones complete
        </span>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-white hover:bg-slate-50 ring-1 ring-slate-200 text-slate-700 text-[12px] font-semibold"
          data-testid="progress-sheet-done"
        >
          Done
        </button>
      </footer>
    </SheetShell>
  );
}

export default ProgressDealSheet;
